/**
 * BackfillStatusPage — /omega/system/backfill
 *
 * Sprint 35 B1: Shows the historical atom-tag backfill runner status.
 * Polls GET /api/v1/admin/backfill/status every 30 s.
 * Founder-only (isOmegaAdmin guard).
 */

import { Navigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { apiFetch } from '@/api/http';

// ── Types ─────────────────────────────────────────────────────────────────────

interface SourceStatus {
  status: string;
  offset: number;
  inserted: number;
}

interface GuardStatus {
  healthcheck_ok: boolean;
  healthcheck_latency_ms: number;
  throttled: boolean;
}

interface BackfillStatus {
  running: boolean;
  paused: boolean;
  current_rate_per_min: number;
  ram_mb: number;
  atoms_inserted_total: number;
  eta_hours: number;
  sources: Record<string, SourceStatus>;
  interference_guards: GuardStatus;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function StatusBadge({ value, trueLabel = 'YES', falseLabel = 'NO', trueGreen = true }: {
  value: boolean;
  trueLabel?: string;
  falseLabel?: string;
  trueGreen?: boolean;
}) {
  const isGood = trueGreen ? value : !value;
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: 4,
      fontWeight: 600,
      fontSize: 13,
      background: isGood ? '#16a34a' : '#dc2626',
      color: '#fff',
    }}>
      {value ? trueLabel : falseLabel}
    </span>
  );
}

function Cell({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <tr>
      <td style={{ padding: '8px 12px', border: '1px solid #e5e7eb', color: '#6b7280', width: 220 }}>
        {label}
      </td>
      <td style={{ padding: '8px 12px', border: '1px solid #e5e7eb' }}>
        {value}
      </td>
    </tr>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function BackfillStatusPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const queryClient = useQueryClient();

  const { data, isLoading, error, dataUpdatedAt } = useQuery({
    queryKey: ['backfill-status'],
    queryFn: () => apiFetch<BackfillStatus>('/admin/backfill/status'),
    refetchInterval: 30_000,
  });

  const pauseMutation = useMutation({
    mutationFn: () => apiFetch('/admin/backfill/pause', { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['backfill-status'] }),
  });

  const resumeMutation = useMutation({
    mutationFn: () => apiFetch('/admin/backfill/resume', { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['backfill-status'] }),
  });

  const mutating = pauseMutation.isPending || resumeMutation.isPending;

  return (
    <div style={{ padding: 24, maxWidth: 900 }}>
      <h1 style={{ marginBottom: 4 }}>Historical Atom-Tag Backfill</h1>
      <p style={{ color: '#6b7280', marginBottom: 20 }}>
        Populates atom_tags.db for the 28M atoms that pre-date A5a-v2.
        {dataUpdatedAt > 0 && (
          <> Last updated: {new Date(dataUpdatedAt).toLocaleTimeString()}.</>
        )}
      </p>

      {isLoading && <p>Loading backfill status…</p>}
      {error && <p style={{ color: '#dc2626' }}>Failed to load backfill status.</p>}

      {data && (
        <>
          {/* ── Controls ─────────────────────────────────────────────────── */}
          <div style={{ marginBottom: 20, display: 'flex', gap: 12 }}>
            <button
              onClick={() => pauseMutation.mutate()}
              disabled={mutating || data.paused}
              style={{
                padding: '8px 20px',
                background: data.paused ? '#9ca3af' : '#dc2626',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                fontWeight: 600,
                cursor: data.paused ? 'not-allowed' : 'pointer',
              }}
            >
              {pauseMutation.isPending ? 'Pausing…' : 'Pause'}
            </button>
            <button
              onClick={() => resumeMutation.mutate()}
              disabled={mutating || !data.paused}
              style={{
                padding: '8px 20px',
                background: !data.paused ? '#9ca3af' : '#16a34a',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                fontWeight: 600,
                cursor: !data.paused ? 'not-allowed' : 'pointer',
              }}
            >
              {resumeMutation.isPending ? 'Resuming…' : 'Resume'}
            </button>
          </div>

          {/* ── Overall status ───────────────────────────────────────────── */}
          <h2 style={{ marginBottom: 8 }}>Runner Status</h2>
          <table style={{ borderCollapse: 'collapse', marginBottom: 28, width: '100%' }}>
            <tbody>
              <Cell label="Running" value={<StatusBadge value={data.running} trueLabel="RUNNING" falseLabel="STOPPED" />} />
              <Cell label="Paused" value={<StatusBadge value={data.paused} trueLabel="PAUSED" falseLabel="ACTIVE" trueGreen={false} />} />
              <Cell label="Atoms inserted (total)" value={data.atoms_inserted_total.toLocaleString()} />
              <Cell label="Rate (atoms/min)" value={data.current_rate_per_min.toLocaleString()} />
              <Cell label="RAM (MiB)" value={data.ram_mb === 0 ? '— (skipped on Windows)' : `${data.ram_mb} MiB`} />
              <Cell
                label="ETA"
                value={data.eta_hours === 0 && data.current_rate_per_min === 0
                  ? '— (not started or rate unknown)'
                  : `${data.eta_hours.toLocaleString()} hours`}
              />
            </tbody>
          </table>

          {/* ── Per-source breakdown ─────────────────────────────────────── */}
          <h2 style={{ marginBottom: 8 }}>Per-Source Progress</h2>
          <table style={{ borderCollapse: 'collapse', marginBottom: 28, width: '100%' }}>
            <thead>
              <tr style={{ background: '#f3f4f6' }}>
                <th style={{ textAlign: 'left', padding: '8px 12px', border: '1px solid #e5e7eb' }}>Source</th>
                <th style={{ padding: '8px 12px', border: '1px solid #e5e7eb' }}>Status</th>
                <th style={{ padding: '8px 12px', border: '1px solid #e5e7eb' }}>Offset</th>
                <th style={{ padding: '8px 12px', border: '1px solid #e5e7eb' }}>Inserted</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(data.sources).length === 0 ? (
                <tr>
                  <td colSpan={4} style={{ padding: '12px', textAlign: 'center', color: '#6b7280', border: '1px solid #e5e7eb' }}>
                    No source progress recorded yet — runner has not started a batch.
                  </td>
                </tr>
              ) : (
                Object.entries(data.sources).map(([name, src]) => (
                  <tr key={name}>
                    <td style={{ padding: '8px 12px', border: '1px solid #e5e7eb', fontWeight: 500 }}>{name}</td>
                    <td style={{ padding: '8px 12px', border: '1px solid #e5e7eb', textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 10px',
                        borderRadius: 4,
                        fontWeight: 600,
                        fontSize: 13,
                        background: src.status === 'done' ? '#16a34a' : src.status === 'running' ? '#2563eb' : '#9ca3af',
                        color: '#fff',
                      }}>
                        {src.status.toUpperCase()}
                      </span>
                    </td>
                    <td style={{ padding: '8px 12px', border: '1px solid #e5e7eb', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {src.offset.toLocaleString()}
                    </td>
                    <td style={{ padding: '8px 12px', border: '1px solid #e5e7eb', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {src.inserted.toLocaleString()}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* ── Guard indicators ─────────────────────────────────────────── */}
          <h2 style={{ marginBottom: 8 }}>Interference Guards</h2>
          <table style={{ borderCollapse: 'collapse', marginBottom: 24, width: '100%' }}>
            <tbody>
              <Cell
                label="Healthcheck"
                value={
                  <>
                    <StatusBadge value={data.interference_guards.healthcheck_ok} trueLabel="OK" falseLabel="DEGRADED" />
                    {' '}
                    <span style={{ color: '#6b7280', fontSize: 13 }}>
                      {data.interference_guards.healthcheck_latency_ms} ms
                    </span>
                  </>
                }
              />
              <Cell
                label="Throttled"
                value={<StatusBadge value={data.interference_guards.throttled} trueLabel="THROTTLED" falseLabel="NORMAL" trueGreen={false} />}
              />
              <Cell label="RAM check" value={<span style={{ color: '#9ca3af' }}>Skipped (Windows — not available)</span>} />
              <Cell label="CPU nice" value={<span style={{ color: '#9ca3af' }}>Skipped (Windows — not available)</span>} />
              <Cell label="DB isolation" value={<span style={{ color: '#16a34a', fontWeight: 600 }}>atom_tags.db only — atoms.db never touched</span>} />
            </tbody>
          </table>

          <p style={{ color: '#6b7280', fontSize: 13 }}>
            Batch size: 100 rows per SQLite transaction (guard b).
            arXiv rate: 1 req/3 s. OpenAlex rate: 5 req/s (200 ms).
          </p>
        </>
      )}
    </div>
  );
}
